<template>
  <div>
    <!-- Stat cards -->
    <div class="stat-row" v-loading="loadingStats">
      <div class="stat-card">
        <div class="stat-card__label">Total Sites</div>
        <div class="stat-card__val">{{ sitesCount }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-card__label">Open Jobs</div>
        <div class="stat-card__val">{{ openJobsCount }}</div>
        <div class="stat-card__sub">
          {{ publishedCount }} published &middot; {{ draftCount }} draft
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-card__label">Devices in Pool</div>
        <div class="stat-card__val">{{ devicesInPool }}</div>
        <div class="stat-card__sub">unassigned</div>
      </div>
      <div class="stat-card" :class="{ 'stat-card--warn': attentionCount > 0 }">
        <div
          class="stat-card__label"
          :class="{ 'text-warn': attentionCount > 0 }"
        >
          Needs Attention
        </div>
        <div
          class="stat-card__val"
          :class="{ 'text-warn': attentionCount > 0 }"
        >
          {{ attentionCount }}
        </div>
        <div v-if="attentionCount > 0" class="stat-card__sub text-warn">
          Action required
        </div>
      </div>
    </div>

    <!-- Two-column content -->
    <div class="dash-grid mt-20">
      <!-- Recent Jobs -->
      <div class="dash-card">
        <div class="dash-card__hdr">
          <span class="dash-card__title">Recent Jobs</span>
          <el-button
            type="text"
            size="small"
            class="show-all-btn"
            @click="$emit('go-jobs')"
          >
            Show all <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>
        <el-table
          :data="recentJobs"
          v-loading="loadingJobs"
          class="dash-table"
          empty-text="No jobs yet"
        >
          <el-table-column prop="name" label="Job Name" min-width="140">
            <template slot-scope="{ row }">
              <el-button
                type="text"
                class="job-link"
                @click="$emit('open-job', row)"
                >{{ row.name }}</el-button
              >
            </template>
          </el-table-column>
          <el-table-column label="Site" min-width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{
              siteNameMap[row.siteId] || '-'
            }}</template>
          </el-table-column>
          <el-table-column prop="status" label="Status" width="110">
            <template slot-scope="{ row }">
              <el-tag :type="statusType(row.status)" size="small">{{
                row.status
              }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="Created" width="130">
            <template slot-scope="{ row }">{{
              formatDate(row.createdAt)
            }}</template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Needs Attention -->
      <div class="dash-card">
        <div class="dash-card__hdr">
          <span class="dash-card__title">
            <i class="el-icon-warning-outline"></i> Needs Attention
          </span>
          <el-button
            type="text"
            size="mini"
            icon="el-icon-refresh"
            @click="loadAttention"
            style="margin-left:auto;padding:0"
          />
        </div>
        <div v-loading="loadingAttention" class="attention-body">
          <!-- Empty state -->
          <div
            class="dash-placeholder"
            v-if="attentionCount === 0 && !loadingAttention"
          >
            <i
              class="el-icon-circle-check dash-placeholder__icon"
              style="color:#67c23a"
            ></i>
            <p class="dash-placeholder__text">
              All clear — nothing needs attention right now.
            </p>
          </div>

          <!-- Escalated jobs -->
          <template v-if="escalatedJobs.length">
            <div class="attention-group-label">Escalated Jobs</div>
            <div
              v-for="job in escalatedJobs"
              :key="'esc-' + job.id"
              class="attention-item"
            >
              <div class="attn-icon attn-icon--red">
                <i class="el-icon-warning"></i>
              </div>
              <div class="attention-item__body">
                <div class="attention-item__title">{{ job.name }}</div>
                <div class="attention-item__sub" v-if="job.scheduledDate">
                  Due {{ formatDate(job.scheduledDate) }}
                </div>
              </div>
              <el-button
                size="mini"
                type="text"
                @click="acknowledgeEscalation(job)"
                :loading="acknowledgingId === job.id"
                >Acknowledge</el-button
              >
              <el-button
                size="mini"
                type="text"
                class="attn-action"
                @click="$emit('open-job', job)"
                >View</el-button
              >
            </div>
          </template>

          <!-- Open issues -->
          <template v-if="openIssues.length">
            <div class="attention-group-label">Open Issues</div>
            <div
              v-for="issue in openIssues"
              :key="'iss-' + issue.id"
              class="attention-item"
            >
              <div class="attn-icon attn-icon--amber">
                <i class="el-icon-warning-outline"></i>
              </div>
              <div class="attention-item__body">
                <div class="attention-item__title">
                  {{ issue.jobName || issue.jobId }}
                </div>
                <div class="attention-item__sub">
                  {{ issueCategoryLabel(issue.category) }}
                </div>
              </div>
              <el-button
                size="mini"
                type="text"
                class="attn-action"
                @click="$emit('open-job', { id: issue.jobId })"
                >View Job</el-button
              >
            </div>
          </template>

          <!-- PQ ready -->
          <template v-if="pqJobs.length">
            <div class="attention-group-label">Pending PQ Sign-off</div>
            <div
              v-for="job in pqJobs"
              :key="'pq-' + job.id"
              class="attention-item"
            >
              <div class="attn-icon attn-icon--blue">
                <i class="el-icon-finished"></i>
              </div>
              <div class="attention-item__body">
                <div class="attention-item__title">{{ job.name }}</div>
                <div class="attention-item__sub" v-if="job.submittedAt">
                  Submitted {{ formatDate(job.submittedAt) }}
                </div>
              </div>
              <el-button
                size="mini"
                type="text"
                class="attn-action"
                @click="$emit('open-job', job)"
                >Review</el-button
              >
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import moment from 'moment'

var ISSUE_CATEGORIES = [
  { value: 'SiteAccessDenied', label: 'Site Access Denied' },
  { value: 'DeviceDamaged', label: 'Device Damaged' },
  { value: 'DeviceMissing', label: 'Device Missing' },
  { value: 'WrongDeviceShipped', label: 'Wrong Device Shipped' },
  { value: 'PowerNotAvailable', label: 'Power Not Available' },
  {
    value: 'NetworkConnectivityProblem',
    label: 'Network Connectivity Problem'
  },
  {
    value: 'MountingLocationUnsuitable',
    label: 'Mounting Location Unsuitable'
  },
  { value: 'CustomerRequestedDelay', label: 'Customer Requested Delay' },
  { value: 'WeatherDelay', label: 'Weather Delay' },
  { value: 'SafetyHazard', label: 'Safety Hazard' },
  { value: 'Other', label: 'Other' }
]

export default {
  name: 'LoraDashboard',
  computed: {
    siteNameMap() {
      var map = {}
      this.sites.forEach(function(s) {
        map[s.id] = s.name
      })
      return map
    },
    openJobsCount() {
      return this.draftCount + this.publishedCount
    },
    draftCount() {
      return this.allJobs.filter(function(j) {
        return j.status === 'Draft'
      }).length
    },
    publishedCount() {
      return this.allJobs.filter(function(j) {
        return j.status === 'Published'
      }).length
    },
    recentJobs() {
      return this.allJobs.slice(0, 5)
    },
    attentionCount() {
      return (
        this.escalatedJobs.length + this.openIssues.length + this.pqJobs.length
      )
    }
  },
  data() {
    return {
      sites: [],
      allJobs: [],
      devicesInPool: 0,
      sitesCount: 0,
      loadingStats: false,
      loadingJobs: false,
      escalatedJobs: [],
      openIssues: [],
      pqJobs: [],
      loadingAttention: false,
      acknowledgingId: null
    }
  },
  mounted() {
    this.loadStats()
    this.loadJobs()
    this.loadAttention()
  },
  methods: {
    async loadStats() {
      this.loadingStats = true
      try {
        var results = await Promise.all([
          LoraApiService.getSites(),
          LoraApiService.getGateways({ status: 'Unassigned' }),
          LoraApiService.getEndDevices({ status: 'Unassigned' })
        ])
        this.sites = results[0].data || []
        this.sitesCount = this.sites.length
        this.devicesInPool =
          (results[1].data || []).length + (results[2].data || []).length
      } catch (e) {
        // stats are non-critical
      } finally {
        this.loadingStats = false
      }
    },

    async loadJobs() {
      this.loadingJobs = true
      try {
        var res = await LoraApiService.getJobs()
        this.allJobs = res.data || []
      } catch (e) {
        this.$message.error('Failed to load jobs')
      } finally {
        this.loadingJobs = false
      }
    },

    async loadAttention() {
      this.loadingAttention = true
      try {
        var pqRes = await LoraApiService.getPendingPqJobs().catch(function() {
          return { data: [] }
        })
        this.pqJobs = pqRes.data || []
      } catch (e) {
        this.pqJobs = []
      }
      try {
        var escRes = await LoraApiService.getEscalatedJobs().catch(function() {
          return { data: [] }
        })
        this.escalatedJobs = escRes.data || []
      } catch (e) {
        this.escalatedJobs = []
      }
      try {
        var issRes = await LoraApiService.getOpenIssues().catch(function() {
          return { data: [] }
        })
        this.openIssues = issRes.data || []
      } catch (e) {
        this.openIssues = []
      }
      this.loadingAttention = false
    },

    async acknowledgeEscalation(job) {
      this.acknowledgingId = job.id
      try {
        await LoraApiService.acknowledgeEscalation(job.id)
        this.$message.success('Escalation acknowledged')
        this.loadAttention()
      } catch (e) {
        this.$message.error('Failed to acknowledge escalation')
      } finally {
        this.acknowledgingId = null
      }
    },

    issueCategoryLabel(cat) {
      var found = ISSUE_CATEGORIES.filter(function(c) {
        return c.value === cat
      })[0]
      return found ? found.label : cat
    },

    statusType(s) {
      return (
        {
          Draft: 'info',
          Published: 'primary',
          Submitted: 'warning',
          Complete: 'success',
          Escalated: 'danger'
        }[s] || 'info'
      )
    },

    formatDate(d) {
      return d ? moment(d).format('DD MMM YYYY') : '-'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

.stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.stat-card {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 14px 16px;

  &--warn {
    border-color: #fde68a;
    background: var(--amber-bg);
  }

  &__label {
    font-size: 12px;
    color: var(--text2);
    margin-bottom: 4px;
  }

  &__val {
    font-size: 24px;
    font-weight: 700;
    color: var(--text);
    line-height: 1;
  }

  &__sub {
    font-size: 11px;
    color: var(--text3);
    margin-top: 4px;
  }
}

.text-warn {
  color: var(--amber) !important;
}

.dash-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.mt-20 {
  margin-top: 20px;
}

.dash-card {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &__hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 16px;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
}

.show-all-btn {
  font-size: 12px;
  padding: 0;
  color: var(--blue);
}

.dash-table {
  width: 100%;

  /deep/ .el-table__header th {
    background: var(--bg2);
    font-size: 11px;
    font-weight: 600;
    color: var(--text2);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 8px 12px;
  }

  /deep/ .el-table__row td {
    padding: 9px 12px;
    font-size: 13px;
  }

  /deep/ .el-table__empty-block {
    padding: 24px 0;
  }

  /deep/ .el-tag {
    border-radius: 99px;
    border: none;
    font-weight: 600;
  }
  /deep/ .el-tag--primary {
    background: var(--blue-bg);
    color: var(--blue);
  }
  /deep/ .el-tag--success {
    background: var(--green-bg);
    color: var(--green);
  }
  /deep/ .el-tag--warning {
    background: var(--amber-bg);
    color: var(--amber);
  }
  /deep/ .el-tag--danger {
    background: var(--red-bg);
    color: var(--red);
  }
  /deep/ .el-tag--info {
    background: var(--bg3);
    color: var(--text2);
  }
}

.job-link {
  font-size: 13px;
  font-weight: 600;
  padding: 0;
  text-align: left;
  color: var(--text);

  &:hover {
    color: var(--blue);
  }
}

// ── Needs Attention ───────────────────────────────────────────────────────
.attention-body {
  flex: 1;
  overflow-y: auto;
  max-height: 340px;
}

.dash-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  gap: 10px;
  color: #c0c4cc;

  &__icon {
    font-size: 32px;
  }

  &__text {
    font-size: 13px;
    color: var(--text2);
    text-align: center;
    max-width: 260px;
    line-height: 1.6;
  }
}

.attention-group-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text2);
  padding: 10px 16px 4px;
}

.attn-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;

  &--blue {
    background: var(--blue-bg);
    color: var(--blue);
  }
  &--amber {
    background: var(--amber-bg);
    color: var(--amber);
  }
  &--red {
    background: var(--red-bg);
    color: var(--red);
  }
}

.attn-action {
  font-size: 12px;
  padding: 0;
  color: var(--blue);
}

.attention-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 16px;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub {
    font-size: 12px;
    color: var(--text2);
    margin-top: 2px;
  }
}
</style>
